import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { ChevronDown, Palette } from 'lucide-react'
import { ColorPicker } from '../shared/ColorPicker'
import type { WidgetConfiguration } from '../../types'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

// Predefined color themes
const COLOR_THEMES = {
  custom: {
    name: 'Custom',
    colors: null, // No preset colors
  },
  ocean_blue: {
    name: 'Ocean Blue',
    description: 'Trust & Clarity',
    colors: {
      header_color: '#0F4C81',
      chat_button_color: '#0F4C81',
      send_button_color: '#0F4C81',
      assistant_chat_bubble_color: '#E3F2FD',
      assistant_chat_font_color: '#1A1A1A',
      profile_chat_bubble_color: '#2196F3',
      profile_chat_font_color: '#FFFFFF',
    },
  },
  forest_green: {
    name: 'Forest Green',
    description: 'Calm & Growth',
    colors: {
      header_color: '#2E7D32',
      chat_button_color: '#2E7D32',
      send_button_color: '#2E7D32',
      assistant_chat_bubble_color: '#F1F8E9',
      assistant_chat_font_color: '#1B1B1B',
      profile_chat_bubble_color: '#66BB6A',
      profile_chat_font_color: '#FFFFFF',
    },
  },
  coral_warmth: {
    name: 'Coral Warmth',
    description: 'Friendly & Approachable',
    colors: {
      header_color: '#E8505B',
      chat_button_color: '#E8505B',
      send_button_color: '#E8505B',
      assistant_chat_bubble_color: '#FFF3F0',
      assistant_chat_font_color: '#2D2D2D',
      profile_chat_bubble_color: '#FF7961',
      profile_chat_font_color: '#FFFFFF',
    },
  },
  slate_professional: {
    name: 'Slate Professional',
    description: 'Modern & Minimal',
    colors: {
      header_color: '#455A64',
      chat_button_color: '#455A64',
      send_button_color: '#455A64',
      assistant_chat_bubble_color: '#F5F5F5',
      assistant_chat_font_color: '#212121',
      profile_chat_bubble_color: '#607D8B',
      profile_chat_font_color: '#FFFFFF',
    },
  },
  sunset_orange: {
    name: 'Sunset Orange',
    description: 'Energetic & Bold',
    colors: {
      header_color: '#F57C00',
      chat_button_color: '#F57C00',
      send_button_color: '#F57C00',
      assistant_chat_bubble_color: '#FFF3E0',
      assistant_chat_font_color: '#2C2C2C',
      profile_chat_bubble_color: '#FF9800',
      profile_chat_font_color: '#FFFFFF',
    },
  },
  teal_modern: {
    name: 'Teal Modern',
    description: 'Tech & Innovation',
    colors: {
      header_color: '#00897B',
      chat_button_color: '#00897B',
      send_button_color: '#00897B',
      assistant_chat_bubble_color: '#E0F2F1',
      assistant_chat_font_color: '#1A1A1A',
      profile_chat_bubble_color: '#26A69A',
      profile_chat_font_color: '#FFFFFF',
    },
  },
  royal_purple: {
    name: 'Royal Purple',
    description: 'Elegant & Creative',
    colors: {
      header_color: '#6B5B95',
      chat_button_color: '#6B5B95',
      send_button_color: '#6B5B95',
      assistant_chat_bubble_color: '#F3F0FF',
      assistant_chat_font_color: '#2D2640',
      profile_chat_bubble_color: '#8B7AB8',
      profile_chat_font_color: '#FFFFFF',
    },
  },
} as const

type ThemeKey = keyof typeof COLOR_THEMES

interface ColorsSectionProps {
  config: WidgetConfiguration
  onUpdate: (updates: Partial<WidgetConfiguration>) => void
  isOpen: boolean
  onToggle: () => void
}

export function ColorsSection({ config, onUpdate, isOpen, onToggle }: ColorsSectionProps) {
  // Detect current theme based on colors
  const detectCurrentTheme = (): ThemeKey => {
    for (const [key, theme] of Object.entries(COLOR_THEMES)) {
      if (key === 'custom' || !theme.colors) continue
      const colors = theme.colors
      if (
        config.header_color === colors.header_color &&
        config.chat_button_color === colors.chat_button_color &&
        config.assistant_chat_bubble_color === colors.assistant_chat_bubble_color &&
        config.profile_chat_bubble_color === colors.profile_chat_bubble_color
      ) {
        return key as ThemeKey
      }
    }
    return 'custom'
  }

  const handleThemeChange = (themeKey: string) => {
    const theme = COLOR_THEMES[themeKey as ThemeKey]
    if (theme?.colors) {
      onUpdate(theme.colors)
    }
  }

  return (
    <Collapsible open={isOpen} onOpenChange={onToggle}>
      <CollapsibleTrigger className="flex w-full items-center justify-between rounded-lg border p-4 hover:bg-accent">
        <h3 className="font-semibold">Colors</h3>
        <ChevronDown
          className={`h-4 w-4 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-4 space-y-4">
        {/* Theme Selector */}
        <div className="space-y-2">
          <label className="text-sm font-medium flex items-center gap-2">
            <Palette className="h-4 w-4" />
            Color Theme
          </label>
          <Select value={detectCurrentTheme()} onValueChange={handleThemeChange}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select a theme" />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(COLOR_THEMES).map(([key, theme]) => (
                <SelectItem key={key} value={key}>
                  <div className="flex items-center gap-2">
                    {theme.colors && (
                      <div className="flex gap-0.5">
                        <div
                          className="w-3 h-3 rounded-full border border-gray-200"
                          style={{ backgroundColor: theme.colors.header_color }}
                        />
                        <div
                          className="w-3 h-3 rounded-full border border-gray-200"
                          style={{ backgroundColor: theme.colors.profile_chat_bubble_color }}
                        />
                      </div>
                    )}
                    <span>{theme.name}</span>
                    {'description' in theme && (
                      <span className="text-xs text-muted-foreground">
                        — {theme.description}
                      </span>
                    )}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            Select a predefined theme or customize individual colors below
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ColorPicker
            label="Header Color"
            value={config.header_color}
            onChange={(color) => onUpdate({ header_color: color })}
            description="Background color of the chat header"
          />

          <ColorPicker
            label="Chat Button Color"
            value={config.chat_button_color}
            onChange={(color) => onUpdate({ chat_button_color: color })}
            description="Color of the chat button"
          />

          <ColorPicker
            label="Send Button Color"
            value={config.send_button_color}
            onChange={(color) => onUpdate({ send_button_color: color })}
            description="Color of the send message button"
          />

          <ColorPicker
            label="Assistant Bubble Color"
            value={config.assistant_chat_bubble_color}
            onChange={(color) => onUpdate({ assistant_chat_bubble_color: color })}
            description="Background of assistant messages"
          />

          <ColorPicker
            label="Assistant Text Color"
            value={config.assistant_chat_font_color}
            onChange={(color) => onUpdate({ assistant_chat_font_color: color })}
            description="Text color for assistant messages"
          />

          <ColorPicker
            label="User Bubble Color"
            value={config.profile_chat_bubble_color}
            onChange={(color) => onUpdate({ profile_chat_bubble_color: color })}
            description="Background of user messages"
          />

          <ColorPicker
            label="User Text Color"
            value={config.profile_chat_font_color}
            onChange={(color) => onUpdate({ profile_chat_font_color: color })}
            description="Text color for user messages"
          />
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}
